import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Send, Loader2, History, CheckCircle2, Clock, CircleDot } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { apiClient } from "@/lib/api/client";
import { useAuth } from "@/hooks/useAuth";
import { resolveRole } from "@/lib/roles";
import type { FeedbackType, FeedbackStatus } from "@/lib/api/types";

const TYPES: { value: FeedbackType; label: string }[] = [
  { value: "bug",             label: "Page error / not working" },
  { value: "inaccurate_data", label: "Inaccurate or wrong data" },
  { value: "access_issue",    label: "Can't access my PLG" },
  { value: "feature_request", label: "Feature request" },
  { value: "other",           label: "Other" },
];

const STATUS_ICON: Record<FeedbackStatus, React.ComponentType<{ className?: string }>> = {
  open:        CircleDot,
  in_progress: Clock,
  resolved:    CheckCircle2,
};

const STATUS_COLOR: Record<FeedbackStatus, string> = {
  open:        "text-health-amber",
  in_progress: "text-blue-600",
  resolved:    "text-health-green",
};

type Tab = "submit" | "history";

export function FeedbackButton() {
  const { user, role: claimsRole } = useAuth();
  const location = useLocation();
  const role = resolveRole(claimsRole, user?.email);

  const [open, setOpen]               = useState(false);
  const [tab, setTab]                 = useState<Tab>("submit");
  const [type, setType]               = useState<FeedbackType>("bug");
  const [description, setDescription] = useState("");
  const [requestedPlg, setRequestedPlg] = useState("");
  const [submitting, setSubmitting]   = useState(false);
  const [submitted, setSubmitted]     = useState(false);
  const [error, setError]             = useState("");

  const { data: historyRes, refetch: refetchHistory } = useQuery({
    queryKey: ["myFeedback", user?.email],
    queryFn:  () => apiClient.getMyFeedback(user!.email!),
    enabled:  !!user?.email && open && tab === "history",
  });

  const { data: plgsRes } = useQuery({
    queryKey: ["plgsForAccessRequest"],
    queryFn:  () => apiClient.getPLGs(),
    enabled:  open && type === "access_issue",
    staleTime: 5 * 60 * 1000,
  });
  const plgOptions = plgsRes?.data ?? [];

  const reset = useCallback(() => {
    setType("bug");
    setDescription("");
    setRequestedPlg("");
    setSubmitted(false);
    setError("");
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setTab("submit");
    reset();
  }, [reset]);

  const openModal = useCallback(() => {
    reset();
    setTab("submit");
    setOpen(true);
  }, [reset]);

  const needsPlg = type === "access_issue";
  const canSubmit = needsPlg ? !!requestedPlg : !!description.trim();

  const submit = useCallback(async () => {
    if (!canSubmit || !user?.email) return;
    setSubmitting(true);
    setError("");
    const res = await apiClient.submitFeedback({
      type,
      description: description.trim(),
      page: location.pathname,
      email: user.email,
      role,
      ...(needsPlg ? { requestedPlg } : {}),
    });
    setSubmitting(false);
    if (res.success) {
      setSubmitted(true);
      void refetchHistory();
    } else {
      setError(res.error ?? "Submission failed. Please try again.");
    }
  }, [canSubmit, description, type, user, role, location.pathname, refetchHistory, needsPlg, requestedPlg]);

  if (!user) return null;

  const history = historyRes?.data ?? [];

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={openModal}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-primary text-primary-foreground px-3 py-2 rounded-full shadow-lg text-xs font-medium hover:bg-primary/90 transition-colors"
        title="Report issue or give feedback"
      >
        <MessageSquarePlus className="w-4 h-4" />
        Feedback
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} />
          <div className="relative bg-background border rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex gap-1">
                <button
                  onClick={() => setTab("submit")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    tab === "submit" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  Submit
                </button>
                <button
                  onClick={() => setTab("history")}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    tab === "history" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  <History className="w-3 h-3" />
                  My submissions
                </button>
              </div>
              <button onClick={close} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-4">
              {/* ── Submit tab ── */}
              {tab === "submit" && (
                <>
                  {submitted ? (
                    <div className="py-6 text-center space-y-2">
                      <p className="text-sm font-medium text-health-green">Received. Thank you.</p>
                      <p className="text-xs text-muted-foreground">Your feedback has been logged and will be reviewed.</p>
                      <div className="flex gap-2 justify-center mt-3">
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { reset(); setTab("history"); }}>
                          View my submissions
                        </Button>
                        <Button size="sm" className="h-8 text-xs" onClick={() => { reset(); }}>
                          Submit another
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Type */}
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</p>
                        <div className="grid grid-cols-1 gap-1">
                          {TYPES.map(t => (
                            <button
                              key={t.value}
                              onClick={() => setType(t.value)}
                              className={`text-left px-3 py-2 rounded-lg text-xs transition-colors border ${
                                type === t.value
                                  ? "bg-primary text-primary-foreground border-primary"
                                  : "border-border hover:bg-muted text-foreground"
                              }`}
                            >
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* PLG picker (access_issue only) */}
                      {needsPlg && (
                        <div className="space-y-1.5">
                          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Which PLG should you have access to?</p>
                          <div className="border rounded-lg overflow-hidden">
                            <Command loop>
                              <CommandInput placeholder="Search PLG (e.g. KCNA 042)..." className="text-xs" />
                              <CommandList className="max-h-40">
                                <CommandEmpty className="text-xs">No PLG found.</CommandEmpty>
                                <CommandGroup>
                                  {plgOptions.map(p => (
                                    <CommandItem
                                      key={p.name}
                                      value={p.name}
                                      onSelect={() => setRequestedPlg(p.name)}
                                      className="text-xs"
                                    >
                                      {p.name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </div>
                          {requestedPlg && (
                            <p className="text-xs text-foreground/70">Selected: <span className="font-mono font-medium">{requestedPlg}</span></p>
                          )}
                        </div>
                      )}

                      {/* Description */}
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Description</p>
                        <textarea
                          rows={3}
                          placeholder={
                            type === "bug"             ? "What happened? What were you trying to do?" :
                            type === "inaccurate_data" ? "Which data looks wrong? What should it show?" :
                            type === "access_issue"    ? "Anything else we should know (optional)?" :
                            type === "feature_request" ? "Describe the feature and why it would help." :
                            "Describe your feedback..."
                          }
                          value={description}
                          onChange={e => setDescription(e.target.value)}
                          className="w-full text-xs border rounded-lg px-3 py-2 bg-background resize-none focus:ring-1 focus:ring-primary outline-none"
                        />
                      </div>

                      <p className="text-xs text-foreground/70">
                        As <span className="font-mono">{user.email}</span> ({role}) · Page: <span className="font-mono">{location.pathname}</span>
                      </p>

                      {error && <p className="text-xs text-health-red">{error}</p>}

                      <Button
                        data-testid="submit-btn"
                        size="sm"
                        className="w-full h-9 text-xs gap-2"
                        onClick={() => { void submit(); }}
                        disabled={!canSubmit || submitting}
                      >
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        {submitting ? "Sending..." : "Submit"}
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* ── History tab ── */}
              {tab === "history" && (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {history.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-8">No submissions yet.</p>
                  ) : (
                    history.map(f => {
                      const StatusIcon = STATUS_ICON[f.status];
                      return (
                        <div key={f.id} className="border rounded-lg p-3 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">
                              {TYPES.find(t => t.value === f.type)?.label ?? f.type}
                            </span>
                            <span className={`flex items-center gap-1 text-xs font-medium ${STATUS_COLOR[f.status]}`}>
                              <StatusIcon className="w-3 h-3" />
                              {f.status.replace("_", " ")}
                            </span>
                          </div>
                          <p className="text-xs">{f.description}</p>
                          {f.resolvedNote && (
                            <p className="text-xs text-foreground/70 bg-muted/50 px-2 py-1 rounded">
                              <span className="font-semibold">Response:</span> {f.resolvedNote}
                            </p>
                          )}
                          <p className="text-xs text-foreground/70 font-mono">
                            {f.createdAt ? new Date(f.createdAt).toLocaleDateString() : ""}
                            {" · "}{f.page}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
